import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  BookOpen,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  CircleDot,
  FileQuestion,
  Eye,
  Loader2,
  Play,
  Send,
  XCircle,
  Trophy,
  Award,
  ArrowRight,
} from "lucide-react";
import axios from "../../../../axios";
import { toast } from "react-toastify";
import { useUser } from "../../../../hooks/use-user";

const getArrayFromResponse = (response) => {
  const data = response?.data;
  const result = data?.result || data?.data;

  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.mockTests)) return result.mockTests;
  if (Array.isArray(result?.questions)) return result.questions;
  if (Array.isArray(data?.mockTests)) return data.mockTests;
  if (Array.isArray(data?.questions)) return data.questions;
  if (Array.isArray(data)) return data;

  return [];
};

const getQuestionsFromStart = (response) => {
  const data = response?.data;
  const result = data?.result || data?.data;

  if (Array.isArray(result?.questions)) return result.questions;
  if (Array.isArray(data?.questions)) return data.questions;
  if (Array.isArray(result)) return result;

  return [];
};

const getOptions = (question) =>
  Array.isArray(question?.options) ? question.options : [];

const getQuestionId = (question, fallback) => question?._id || String(fallback);

const pickFirst = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const getSelectedStateId = (userData) => {
  const selectedState =
    userData?.selectedState?._id ||
    userData?.selectedState ||
    userData?.state?._id ||
    userData?.state ||
    userData?.stateId ||
    localStorage.getItem("stateId");

  return selectedState ? String(selectedState) : "";
};

const normalizeText = (value) =>
  value === undefined || value === null ? "" : String(value).trim();

const normalizeForCompare = (value) => normalizeText(value).toLowerCase();

const getResultPayload = (response) => {
  const data = response?.data;
  const result = data?.result || data?.data;

  return result?.attempt || result || data || {};
};

const pickLeaderboardRows = (response) => {
  const data = response?.data;
  const result = data?.result || data?.data;
  const candidates = [
    result?.leaderboard,
    result?.users,
    result?.topUsers,
    result?.results,
    result?.attempts,
    result,
    data?.leaderboard,
    data?.users,
    data?.topUsers,
    data,
  ];

  return candidates.find((item) => Array.isArray(item)) || [];
};

const getLeaderName = (row) => {
  const user = row?.user || row?.userId || row?.student || {};

  return pickFirst(
    row?.name,
    row?.userName,
    row?.studentName,
    user?.name,
    user?.fullName,
    "User",
  );
};

const getLeaderScore = (row) =>
  pickFirst(row?.score, row?.marks, row?.totalScore, row?.finalScore, 0);

const pickCandidateArray = (...candidates) => {
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
  }

  return [];
};

const pickResultRows = (payload) => {
  const rows = pickCandidateArray(
    payload?.result,
    payload?.data,
    payload?.questions,
    payload?.answerKey,
    payload?.answers,
    payload?.responses,
    payload?.records,
    payload?.items,
    payload?.myResult,
    payload?.submission?.answers,
    payload?.submission?.questions,
    payload?.attempt?.questions,
    payload?.attempt?.answers,
    payload?.attempt?.responses,
    payload?.result?.result,
    payload?.data?.result,
    payload?.result?.questions,
    payload?.result?.answers,
    payload?.data?.questions,
    payload?.data?.answers,
    payload,
  );

  return rows.flatMap((row) => (Array.isArray(row) ? row : [row]));
};

const getResultSummary = (payload, rows) => {
  if (!payload) return {};

  const objectPayload =
    !Array.isArray(payload) && typeof payload === "object" ? payload : {};
  const nestedResult =
    objectPayload?.result &&
    !Array.isArray(objectPayload.result) &&
    typeof objectPayload.result === "object"
      ? objectPayload.result
      : {};
  const nestedData =
    objectPayload?.data &&
    !Array.isArray(objectPayload.data) &&
    typeof objectPayload.data === "object"
      ? objectPayload.data
      : {};
  const rowSummary =
    rows.find((row) =>
      Boolean(
        pickFirst(
          row?.attemptStatus,
          row?.submittedAt,
          row?.totalQuestions,
          row?.score,
          row?.correct,
          row?.wrong,
          row?.skipped,
        ),
      ),
    ) || rows[0] || {};

  return {
    ...rowSummary,
    ...nestedData,
    ...nestedResult,
    ...objectPayload,
  };
};

const getQuestionSource = (item) => {
  if (item?.question && typeof item.question === "string") return item;

  return (
    item?.question ||
    item?.mockQuestion ||
    item?.mockTestQuestion ||
    item?.questionData ||
    item
  );
};

const resolveResultQuestion = (item, index) => {
  const source = getQuestionSource(item);
  const question = pickFirst(
    item?.questionText,
    source?.question,
    source?.questionEng,
    source?.questionHin,
    `Question ${index + 1}`,
  );
  const correctAnswer = pickFirst(
    item?.correctAnswer,
    item?.rightAnswer,
    item?.rightAnsEng,
    source?.answer,
    source?.correctAnswer,
    source?.answerHin,
    "",
  );
  const userAnswer = pickFirst(
    item?.userAnswer,
    item?.selectedAnswer,
    item?.submittedAnswer,
    item?.answer,
    item?.selectedOption,
    "",
  );
  const type = normalizeText(item?.type || item?.status).toLowerCase();
  const isSkipped =
    item?.isSkipped === true ||
    item?.skipped === true ||
    type === "skip" ||
    type === "skipped" ||
    !normalizeText(userAnswer);
  const isCorrect =
    !isSkipped &&
    (item?.isCorrect === true ||
      item?.ansRight === true ||
      normalizeForCompare(userAnswer) === normalizeForCompare(correctAnswer));
  const options = Array.isArray(source?.options)
    ? source.options
    : [source?.optionA, source?.optionB, source?.optionC, source?.optionD].filter(
        (option) => option !== undefined && option !== null,
      );

  return {
    id: item?._id || source?._id || index,
    number: item?.questionNo || item?.number || index + 1,
    question: normalizeText(question),
    options,
    correctAnswer: normalizeText(correctAnswer),
    userAnswer: normalizeText(userAnswer),
    isSkipped,
    isCorrect,
  };
};

const formatTime = (seconds) => {
  const totalSeconds = Math.max(Number(seconds || 0), 0);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;

  if (hours > 0) {
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0",
    )}:${String(secs).padStart(2, "0")}`;
  }

  return `${String(minutes).padStart(2, "0")}:${String(secs).padStart(
    2,
    "0",
  )}`;
};

export default function MockTestFlow() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: user } = useUser();
  const [mockTests, setMockTests] = useState([]);
  const [selectedMockTest, setSelectedMockTest] = useState(null);
  const [selectedResult, setSelectedResult] = useState(null);
  const [selectedAnswerKey, setSelectedAnswerKey] = useState(null);
  const [leaderboardRows, setLeaderboardRows] = useState([]);
  const [resultsByMockTestId, setResultsByMockTestId] = useState({});
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [visitedQuestionIds, setVisitedQuestionIds] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [leaderboardLoadingId, setLeaderboardLoadingId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [mockTestDeadlineMs, setMockTestDeadlineMs] = useState(null);
  const [activeAttemptId, setActiveAttemptId] = useState("");
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const submitLockRef = useRef(false);
  const autoStartRef = useRef(false);
  const autoSubmitTriggeredRef = useRef(false);
  const [mode, setMode] = useState("info");
  const [questionLanguage, setQuestionLanguage] = useState("en");

  const currentQuestion = questions[currentIndex];
  const answeredCount = Object.keys(answers).length;

  const rawResultRows = useMemo(
    () => pickResultRows(selectedAnswerKey || selectedResult),
    [selectedAnswerKey, selectedResult],
  );

  const resultRows = useMemo(
    () =>
      rawResultRows.map((item, index) =>
        resolveResultQuestion(item, index),
      ),
    [rawResultRows],
  );

  const resultStats = useMemo(() => {
    const summary = getResultSummary(selectedResult, rawResultRows);
    const correct = Number(
      pickFirst(
        summary?.correct,
        summary?.rightAnswers,
        summary?.correctAnswers,
        summary?.correctCount,
        summary?.rightCount,
        selectedResult?.correct,
        selectedResult?.rightAnswers,
        selectedResult?.correctAnswers,
        selectedResult?.correctCount,
        selectedResult?.rightCount,
        resultRows.filter((row) => row.isCorrect).length,
      ),
    );
    const wrong = Number(
      pickFirst(
        summary?.wrong,
        summary?.wrongAnswers,
        summary?.incorrectAnswers,
        summary?.incorrectCount,
        summary?.wrongCount,
        selectedResult?.wrong,
        selectedResult?.wrongAnswers,
        selectedResult?.incorrectAnswers,
        selectedResult?.incorrectCount,
        selectedResult?.wrongCount,
        resultRows.filter((row) => !row.isCorrect && !row.isSkipped).length,
      ),
    );
    const skipped = Number(
      pickFirst(
        summary?.skipped,
        summary?.skippedAnswers,
        summary?.skipCount,
        selectedResult?.skipped,
        selectedResult?.skippedAnswers,
        selectedResult?.skipCount,
        resultRows.filter((row) => row.isSkipped).length,
      ),
    );
    const total = Number(
      pickFirst(
        summary?.totalQuestions,
        summary?.total,
        selectedResult?.totalQuestions,
        selectedResult?.total,
        resultRows.length,
      ),
    );
    const score = pickFirst(
      summary?.score,
      summary?.marks,
      summary?.totalScore,
      summary?.finalScore,
      selectedResult?.score,
      selectedResult?.marks,
      selectedResult?.totalScore,
      selectedResult?.finalScore,
      correct,
    );

    return { correct, wrong, skipped, total, score };
  }, [rawResultRows, resultRows, selectedResult]);

  const fetchMockTests = useCallback(async () => {
    setLoading(true);

    try {
      const stateId = searchParams.get("stateId") || getSelectedStateId(user);
      const response = await axios.get("/mock-tests/", {
        params: {
          courseId,
          ...(stateId ? { stateId } : {}),
        },
      });
      let courseMockTests = getArrayFromResponse(response);

      if (!courseMockTests.length) {
        const startMockTestId = searchParams.get("start");
        const allResponse = await axios.get("/mock-tests/all");
        courseMockTests = getArrayFromResponse(allResponse).filter(
          (mockTest) =>
            String(mockTest?.course?._id || mockTest?.course || "") ===
              String(courseId) ||
            String(mockTest?._id || mockTest?.id || "") === String(startMockTestId),
        );
      }

      setMockTests(courseMockTests);
      setSelectedMockTest(courseMockTests[0] || null);
    } catch (error) {
      toast.error("Unable to load mock tests");
    } finally {
      setLoading(false);
    }
  }, [courseId, searchParams, user]);

  useEffect(() => {
    fetchMockTests();
  }, [fetchMockTests]);

  useEffect(() => {
    if (!mockTests.length) return undefined;

    let isActive = true;

    const fetchSubmittedResults = async () => {
      const settled = await Promise.allSettled(
        mockTests.map(async (mockTest) => {
          const response = await axios.get(
            `/mock-tests/${mockTest._id}/my-result`,
            { params: user?._id ? { userId: user._id } : {} },
          );

          return [mockTest._id, getResultPayload(response)];
        }),
      );

      if (!isActive) return;

      const nextResults = {};
      settled.forEach((item) => {
        if (item.status !== "fulfilled") return;

        const [mockTestId, payload] = item.value;
        if (payload && Object.keys(payload).length) {
          nextResults[mockTestId] = payload;
        }
      });

      setResultsByMockTestId(nextResults);
    };

    fetchSubmittedResults();

    return () => {
      isActive = false;
    };
  }, [mockTests, user?._id]);

  const fetchQuestions = useCallback(async (mockTestId) => {
    const response = await axios.get(`/mock-tests/${mockTestId}/questions`);
    return getArrayFromResponse(response);
  }, []);

  const fetchLeaderboard = useCallback(async (mockTest) => {
    setLeaderboardLoadingId(mockTest._id);

    try {
      const response = await axios.get(`/mock-tests/${mockTest._id}/leaderboard`);

      setSelectedMockTest(mockTest);
      setLeaderboardRows(pickLeaderboardRows(response));
      setMode("leaderboard");
    } catch (error) {
      toast.error("Unable to load leaderboard");
    } finally {
      setLeaderboardLoadingId("");
    }
  }, []);

  const handleStartTest = useCallback(async (mockTest) => {
    try {
      const startResponse = await axios.post(`/mock-tests/${mockTest._id}/start`);
      const startPayload =
        startResponse?.data?.result || startResponse?.data?.data || startResponse?.data || {};
      const startQuestions = getQuestionsFromStart(startResponse);
      const questionList = startQuestions.length
        ? startQuestions
        : await fetchQuestions(mockTest._id);

      if (!questionList.length) {
        toast.error("No questions are available for this mock test");
        return;
      }

      setSelectedMockTest(mockTest);
      setQuestions(questionList);
      setAnswers({});
      setCurrentIndex(0);
      setVisitedQuestionIds([getQuestionId(questionList[0], 0)]);
      const durationSeconds = Math.max(Number(mockTest?.durationMinutes || 0), 0) * 60;
      const expiresAtMs = startPayload?.expiresAt
        ? new Date(startPayload.expiresAt).getTime()
        : Date.now() + durationSeconds * 1000;
      const attemptId =
        startPayload?.attempt?._id ||
        startPayload?.attemptId ||
        startPayload?.attempt ||
        startPayload?.record?._id ||
        startPayload?.submission?._id ||
        startPayload?._id ||
        "";

      setMockTestDeadlineMs(expiresAtMs);
      setActiveAttemptId(String(attemptId || ""));
      setTimeLeft(Math.max(Math.ceil((expiresAtMs - Date.now()) / 1000), 0));
      setShowSubmitConfirm(false);
      setQuestionLanguage("en");
      submitLockRef.current = false;
      autoSubmitTriggeredRef.current = false;
      setMode("questions");
    } catch (error) {
      toast.error("Unable to start mock test");
    }
  }, [fetchQuestions]);

  useEffect(() => {
    const startMockTestId = searchParams.get("start");

    if (!startMockTestId || autoStartRef.current || !mockTests.length) return;

    const mockTest = mockTests.find(
      (item) => String(item?._id) === String(startMockTestId),
    );

    if (!mockTest) return;

    autoStartRef.current = true;
    handleStartTest(mockTest);
  }, [handleStartTest, mockTests, searchParams]);

  const handleAnswer = (question, answer) => {
    const questionId = getQuestionId(question, currentIndex);

    setAnswers((prev) => ({
      ...prev,
      [questionId]: {
        question: questionId,
        answer,
      },
    }));
  };

  const handleSubmit = useCallback(async ({ auto = false } = {}) => {
    if (!selectedMockTest?._id) return;
    if (submitLockRef.current) return;

    submitLockRef.current = true;
    setSubmitting(true);
    setShowSubmitConfirm(false);

    try {
      await axios.post(`/mock-tests/${selectedMockTest._id}/submit`, {
        ...(activeAttemptId
          ? {
              attempt: activeAttemptId,
              attemptId: activeAttemptId,
            }
          : {}),
        answers: Object.values(answers),
      });
      toast.success(
        auto
          ? "Time up! Mock test submitted automatically"
          : "Mock test submitted successfully",
      );
      navigate(`/dash/mock-test-result/${selectedMockTest._id}`);
    } catch (error) {
      toast.error("Unable to submit mock test");
      if (!auto) {
        submitLockRef.current = false;
      }
    } finally {
      setSubmitting(false);
    }
  }, [activeAttemptId, answers, navigate, selectedMockTest]);

  const handleSubmitClick = () => {
    if (submitting) return;

    if (currentIndex !== questions.length - 1) {
      toast.info("Please go to the last question to submit the test");
      return;
    }

    setShowSubmitConfirm(true);
  };

  useEffect(() => {
    if (
      mode !== "questions" ||
      !selectedMockTest?._id ||
      !mockTestDeadlineMs ||
      submitting
    ) {
      return undefined;
    }

    const syncTimer = () => {
      const secondsLeft = Math.max(
        Math.ceil((mockTestDeadlineMs - Date.now()) / 1000),
        0,
      );

      setTimeLeft(secondsLeft);

      if (secondsLeft <= 0 && !autoSubmitTriggeredRef.current) {
        autoSubmitTriggeredRef.current = true;
        handleSubmit({ auto: true });
      }
    };

    syncTimer();
    const interval = setInterval(syncTimer, 1000);

    return () => clearInterval(interval);
  }, [
    handleSubmit,
    mockTestDeadlineMs,
    mode,
    selectedMockTest?._id,
    submitting,
  ]);

  useEffect(() => {
    if (mode !== "questions" || !currentQuestion) return;

    const questionId = getQuestionId(currentQuestion, currentIndex);

    setVisitedQuestionIds((prev) =>
      prev.includes(questionId) ? prev : [...prev, questionId],
    );
  }, [currentIndex, currentQuestion, mode]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F8FC] px-3 py-6 sm:px-5 sm:py-10">
        <div className="mx-auto max-w-4xl rounded-[1.5rem] border border-[#E8E3EE] bg-white p-6 text-center shadow-[0_8px_24px_rgba(48,31,61,0.05)]">
          Loading mock tests...
        </div>
      </div>
    );
  }

  if (mode === "leaderboard") {
    return (
      <div className="min-h-screen bg-[#F7F8FC] px-3 py-4 sm:px-5 sm:py-6">
        <div className="mx-auto max-w-5xl">
          <button
            type="button"
            className="mb-4 inline-flex min-h-10 items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-bold text-[#4B2D83] shadow-sm"
            onClick={() => setMode("info")}
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>

          <div className="rounded-[1.5rem] bg-[#562566] p-5 text-white shadow-[0_10px_24px_rgba(86,37,102,0.18)] sm:p-7">
            <p className="text-sm font-bold uppercase text-[#FFE3A5]">
              Mock Test Leaderboard
            </p>
            <h1 className="mt-2 text-2xl font-extrabold sm:text-3xl">
              {selectedMockTest?.title || "Mock Test"}
            </h1>
          </div>

          <div className="mt-5 overflow-hidden rounded-[1.5rem] border border-[#E8E3EE] bg-white shadow-sm">
            <div className="grid grid-cols-[52px_minmax(0,1fr)_76px] bg-[#F1F2F7] px-4 py-3 text-[11px] font-bold uppercase text-gray-600 sm:grid-cols-[70px_1fr_120px] sm:text-xs">
              <span>Rank</span>
              <span>User</span>
              <span className="text-right">Score</span>
            </div>
            {leaderboardRows.map((row, index) => (
              <div
                key={row?._id || row?.userId || index}
                className="grid grid-cols-[52px_minmax(0,1fr)_76px] border-t border-gray-100 px-4 py-3 text-sm sm:grid-cols-[70px_1fr_120px]"
              >
                <span className="font-extrabold text-purple">
                  #{pickFirst(row?.rank, index + 1)}
                </span>
                <span className="font-semibold text-[#34163F]">
                  {getLeaderName(row)}
                </span>
                <span className="text-right font-extrabold text-[#34163F]">
                  {getLeaderScore(row)}
                </span>
              </div>
            ))}
            {!leaderboardRows.length && (
              <div className="px-4 py-10 text-center text-gray-500">
                Leaderboard abhi available nahi hai.
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (mode === "result") {
    return (
      <div className="min-h-screen bg-[#F7F8FC] px-3 py-4 sm:px-5 sm:py-6">
        <div className="mx-auto max-w-5xl">
          <button
            type="button"
            className="mb-4 inline-flex min-h-10 items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-bold text-[#4B2D83] shadow-sm"
            onClick={() => {
              setMode("info");
              setSelectedResult(null);
              setSelectedAnswerKey(null);
            }}
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>

          <div className="rounded-[1.5rem] bg-[#562566] p-5 text-white shadow-[0_10px_24px_rgba(86,37,102,0.18)] sm:p-7">
            <p className="text-sm font-bold uppercase text-[#FFE3A5]">
              Mock Test Result
            </p>
            <h1 className="mt-2 text-2xl font-extrabold sm:text-3xl">
              {selectedMockTest?.title || selectedResult?.mockTest?.title || "Mock Test"}
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-purple-100">
              Apna score aur answer key review karein.
            </p>
            {selectedMockTest?._id && (
              <button
                type="button"
                disabled={leaderboardLoadingId === selectedMockTest._id}
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-[#F6A91A] px-5 py-2 text-sm font-extrabold text-[#34163F] disabled:opacity-70"
                onClick={() => fetchLeaderboard(selectedMockTest)}
              >
                <Trophy className="h-4 w-4" />
                {leaderboardLoadingId === selectedMockTest._id
                  ? "Loading..."
                  : "Leaderboard"}
              </button>
            )}
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {[
              ["Score", resultStats.score, Trophy, "bg-amber-100 text-amber-700"],
              ["Total", resultStats.total, FileQuestion, "bg-purple-100 text-purple"],
              ["Correct", resultStats.correct, CheckCircle2, "bg-green-100 text-green-700"],
              ["Wrong", resultStats.wrong, XCircle, "bg-red-100 text-red-700"],
              ["Skipped", resultStats.skipped, CircleDot, "bg-gray-100 text-gray-700"],
            ].map(([label, value, Icon, tone]) => (
              <div key={label} className="rounded-[1.25rem] border border-[#E8E3EE] bg-white p-4 shadow-sm">
                <div className={`mb-3 inline-flex h-10 w-10 items-center justify-center rounded-full ${tone}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <p className="text-sm font-bold text-gray-500">{label}</p>
                <p className="mt-1 text-2xl font-extrabold text-[#34163F]">
                  {value ?? 0}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-16 space-y-4">
            {resultRows.map((question) => (
              <div
                key={question.id}
                className="rounded-[1.25rem] border border-[#E8E3EE] bg-white p-4 shadow-sm sm:p-5"
              >
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <span className="rounded-full bg-purple-100 px-3 py-1 text-sm font-extrabold text-purple">
                    Q{question.number}
                  </span>
                  <span
                    className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-bold ${
                      question.isSkipped
                        ? "bg-gray-100 text-gray-700"
                        : question.isCorrect
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                    }`}
                  >
                    {question.isSkipped ? (
                      <CircleDot className="h-4 w-4" />
                    ) : question.isCorrect ? (
                      <CheckCircle2 className="h-4 w-4" />
                    ) : (
                      <XCircle className="h-4 w-4" />
                    )}
                    {question.isSkipped ? "Skipped" : question.isCorrect ? "Correct" : "Wrong"}
                  </span>
                </div>

                <h2 className="text-base font-bold leading-7 text-[#24112D] sm:text-lg">
                  {question.question}
                </h2>

                <div className="mt-4 grid gap-2">
                  {question.options.map((option, index) => {
                    const optionText = normalizeText(option);
                    const isCorrect =
                      normalizeForCompare(optionText) ===
                      normalizeForCompare(question.correctAnswer);
                    const isUserAnswer =
                      normalizeForCompare(optionText) ===
                      normalizeForCompare(question.userAnswer);
                    const isWrongUserAnswer =
                      isUserAnswer && !isCorrect && !question.isSkipped;

                    return (
                      <div
                        key={`${question.id}-${index}`}
                        className={`flex min-h-[52px] items-center gap-3 rounded-lg border px-4 py-3 text-left font-semibold ${
                          isCorrect
                            ? "border-green-200 bg-green-50 text-green-800"
                            : isWrongUserAnswer
                              ? "border-red-200 bg-red-50 text-red-800"
                              : "border-gray-200 bg-white text-gray-800"
                        }`}
                      >
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-sm font-extrabold text-gray-600">
                          {String.fromCharCode(65 + index)}
                        </span>
                        <span className="min-w-0 flex-1 break-words">
                          {optionText}
                        </span>
                        {isCorrect && (
                          <span className="text-xs font-extrabold text-green-700">
                            Correct
                          </span>
                        )}
                        {isWrongUserAnswer && (
                          <span className="text-xs font-extrabold text-red-700">
                            Your Answer
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>

                {!question.options.length && (
                  <div className="mt-4 grid gap-2 text-sm font-semibold">
                    <p className="rounded-lg bg-red-50 px-4 py-3 text-red-700">
                      Your Answer: {question.userAnswer || "Skipped"}
                    </p>
                    <p className="rounded-lg bg-green-50 px-4 py-3 text-green-700">
                      Correct Answer: {question.correctAnswer || "-"}
                    </p>
                  </div>
                )}
              </div>
            ))}

            {!resultRows.length && (
              <div className="rounded-lg border border-gray-200 bg-white p-6 text-center shadow-sm">
                <h2 className="text-xl font-bold text-[#34163F]">
                  Answer key is not available yet
                </h2>
                <p className="mt-2 text-sm text-gray-600">
                  Result mil gaya hai, lekin question-wise answer key API response me nahi aayi.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (mode === "questions" && currentQuestion) {
    const questionId = getQuestionId(currentQuestion, currentIndex);
    const selectedAnswer = answers[questionId]?.answer || "";
    const options = getOptions(currentQuestion);
    const hindiOptions = Array.isArray(currentQuestion.optionsHin)
      ? currentQuestion.optionsHin
      : [];
    const displayedOptions =
      questionLanguage === "hi" && hindiOptions.length === options.length
        ? hindiOptions
        : options;
    const progressPercent = questions.length
      ? Math.round((answeredCount / questions.length) * 100)
      : 0;
    const timerDanger = timeLeft <= 60;
    const visitedCount = visitedQuestionIds.length;
    const unansweredCount = Math.max(questions.length - answeredCount, 0);
    const notVisitedCount = Math.max(questions.length - visitedCount, 0);
    const isLastQuestion = currentIndex === questions.length - 1;
    const visiblePaletteQuestions = questions;

    return (
      <div className="min-h-screen overflow-x-hidden bg-[#F7F8FC] px-3 py-3 sm:px-5 sm:py-5 lg:mt-10">
        <div className="mx-auto min-w-0 max-w-6xl">
         
          <section className="sticky top-12 z-40 mt-3 min-w-0 rounded-[1.5rem] border border-[#E8E3EE] bg-white p-4 shadow-[0_8px_24px_rgba(48,31,61,0.12)] sm:p-5 lg:top-20">
            <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <h2 className="text-lg font-extrabold text-[#211629] sm:text-2xl">
                  {selectedMockTest?.title || "Mock Test"}
                </h2>
                <p className="mt-1 text-sm font-semibold text-gray-500 sm:text-base">
                  Question {currentIndex + 1} of {questions.length}
                </p>
              </div>
              <div
                className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-2 text-xs font-extrabold sm:gap-2 sm:px-3 sm:text-sm ${
                  timerDanger
                    ? "bg-red-50 text-red-600"
                    : "bg-[#F1EDF9] text-[#4B2D83]"
                }`}
              >
                <Clock className="h-4 w-4" />
                <span className="tabular-nums">{formatTime(timeLeft)} Left</span>
              </div>
            </div>

            <div className="mt-5 flex items-center gap-5 sm:gap-8">
              <div className="flex items-center gap-2">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#E6F7F1] text-[#159A78]">
                  <CheckCircle2 className="h-5 w-5" />
                </span>
                <span className="text-base font-bold text-[#4B4652]">{answeredCount}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#FDECEF] text-[#D84B59]">
                  <XCircle className="h-5 w-5" />
                </span>
                <span className="text-base font-bold text-[#4B4652]">{unansweredCount}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#FFF3DF] text-[#E79A25]">
                  <CircleDot className="h-5 w-5" />
                </span>
                <span className="text-base font-bold text-[#4B4652]">{notVisitedCount}</span>
              </div>
            </div>

            <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-[#EDE4F1]">
              <div className="h-full rounded-full bg-[#F6A91A] transition-all" style={{ width: `${progressPercent}%` }} />
            </div>
          </section>
        </div>

        <div className="mx-auto mt-4 grid min-w-0 max-w-6xl gap-4 lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-5">
          <main className="order-2 min-w-0 rounded-[1.5rem] border border-[#E8E3EE] bg-white p-3 shadow-[0_8px_24px_rgba(48,31,61,0.05)] sm:p-5 lg:order-1 lg:p-6">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-xs font-bold text-gray-500">
              <span className="text-sm font-bold text-[#211629]">
                Question {currentIndex + 1}
              </span>
              {currentQuestion.quesType && (
                <span className="rounded-full bg-amber-50 px-3 py-1 text-[#9A5A00]">
                  {currentQuestion.quesType}
                </span>
              )}
            </div>

            <div className="rounded-2xl bg-[#F1F2F7] p-4 sm:p-5">
              <div className="mb-3 flex justify-end">
                <div className="inline-flex rounded-full border border-[#E2DCEB] bg-white p-1 text-xs font-extrabold">
                  <button
                    type="button"
                    className={`rounded-full px-4 py-2 transition ${questionLanguage === "en" ? "bg-[#562566] text-white" : "text-[#6E6875]"}`}
                    onClick={() => setQuestionLanguage("en")}
                  >
                    EN
                  </button>
                  <button
                    type="button"
                    disabled={!currentQuestion.questionHin}
                    className={`rounded-full px-4 py-2 transition disabled:cursor-not-allowed disabled:opacity-50 ${questionLanguage === "hi" ? "bg-[#562566] text-white" : "text-[#6E6875]"}`}
                    onClick={() => setQuestionLanguage("hi")}
                  >
                    हिंदी
                  </button>
                </div>
              </div>
              <p className="text-base font-bold leading-7 text-[#24112D] sm:text-lg sm:leading-8">
                {questionLanguage === "hi" && currentQuestion.questionHin
                  ? currentQuestion.questionHin
                  : currentQuestion.question}
              </p>
            </div>

            <div className="mt-4 grid gap-2.5 sm:mt-5">
              {displayedOptions.map((option, index) => {
                const answerValue = options[index] ?? option;
                const isSelected = selectedAnswer === answerValue;

                return (
                  <button
                    key={`${option}-${index}`}
                    type="button"
                    className={`flex min-h-[58px] items-center gap-3 rounded-xl border px-3.5 py-3.5 text-left font-semibold transition sm:px-4 ${
                      isSelected
                        ? "border-[#562566] bg-[#F3ECF7] text-[#2D1638] shadow-sm"
                        : "border-gray-200 bg-white text-gray-800 hover:border-[#562566] hover:bg-[#FCF9FD]"
                    }`}
                    onClick={() => handleAnswer(currentQuestion, answerValue)}
                  >
                    <span
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-extrabold ${
                        isSelected
                          ? "bg-[#562566] text-white"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {String.fromCharCode(65 + index)}
                    </span>
                    <span className="min-w-0 break-words">{option}</span>
                  </button>
                );
              })}
            </div>

            <div className="mt-5 flex items-center justify-between gap-2 sm:mt-6 sm:gap-3">
              <button
                type="button"
                className="inline-flex min-h-11 min-w-[110px] items-center justify-center gap-1.5 rounded-xl border border-gray-300 px-3 py-2.5 text-sm font-bold text-gray-700 disabled:opacity-50 sm:min-w-[140px] sm:gap-2 sm:px-5"
                disabled={currentIndex === 0}
                onClick={() => setCurrentIndex((index) => index - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </button>

              {isLastQuestion ? (
                <button
                  type="button"
                  disabled={submitting}
                  className="inline-flex min-h-11 min-w-[122px] items-center justify-center gap-1.5 rounded-xl bg-[#F2A51A] px-3 py-2.5 text-sm font-extrabold text-white shadow-sm disabled:opacity-70 sm:min-w-[150px] sm:gap-2 sm:px-5"
                  onClick={handleSubmitClick}
                >
                  <Send className="h-4 w-4" />
                  {submitting ? "Submitting..." : "Submit Test"}
                </button>
              ) : (
                <button
                  type="button"
                  className="inline-flex min-h-11 min-w-[122px] items-center justify-center gap-1.5 rounded-xl bg-[#F2A51A] px-3 py-2.5 text-sm font-extrabold text-white shadow-sm sm:min-w-[150px] sm:gap-2 sm:px-5"
                  onClick={() => setCurrentIndex((index) => index + 1)}
                >
                  Next Question
                  <ChevronRight className="h-4 w-4" />
                </button>
              )}
            </div>
          </main>

          <aside className="order-1 min-w-0 rounded-[1.5rem] border border-[#E8E3EE] bg-white p-4 shadow-[0_8px_24px_rgba(48,31,61,0.05)] lg:sticky lg:top-28 lg:order-2 lg:self-start lg:p-6">
            <div className="mb-3 flex items-start justify-between gap-3">
              <h2 className="text-base font-extrabold text-[#2D1638] lg:text-sm">
                Question Palette
                <span className="mt-1 block text-[11px] font-bold text-gray-500">
                  1-{questions.length} of {questions.length}
                </span>
              </h2>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  disabled={submitting || !isLastQuestion}
                  className={`hidden text-xs font-extrabold lg:block lg:text-sm ${
                    isLastQuestion ? "text-[#562566]" : "text-[#6e6875]"
                  }`}
                  onClick={handleSubmitClick}
                >
                  {submitting ? "Submitting..." : "Submit Test"}
                </button>
              </div>
            </div>

            <div className="-mx-1 flex snap-x snap-mandatory touch-pan-x gap-2 overflow-x-auto overscroll-x-contain px-1 pb-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden lg:hidden">
              {visiblePaletteQuestions.map((question, localIndex) => {
                const index = localIndex;
                const itemId = getQuestionId(question, index);
                const isAnswered = Boolean(answers[itemId]);
                const isActive = index === currentIndex;
                const isSkipped = visitedQuestionIds.includes(itemId) && !isAnswered;

                return (
                  <button
                    key={itemId}
                    type="button"
                    className={`flex h-12 w-12 shrink-0 snap-start items-center justify-center rounded-2xl text-sm font-extrabold transition ${
                      isActive
                        ? "bg-[#562566] text-white"
                        : isAnswered
                          ? "bg-[#DFF8EC] text-green-700"
                          : isSkipped
                            ? "bg-red-100 text-red-600"
                            : "bg-[#F4F1F8] text-[#6E6875] hover:bg-[#EADFEF]"
                    }`}
                    onClick={() => setCurrentIndex(index)}
                  >
                    {index + 1}
                  </button>
                );
              })}
            </div>

            <div className="hidden grid-cols-7 gap-2 lg:grid lg:grid-cols-5 lg:justify-items-center lg:gap-3">
              {visiblePaletteQuestions.map((question, localIndex) => {
                const index = localIndex;
                const itemId = getQuestionId(question, index);
                const isAnswered = Boolean(answers[itemId]);
                const isActive = index === currentIndex;
                const isSkipped = visitedQuestionIds.includes(itemId) && !isAnswered;

                return (
                  <button
                    key={itemId}
                    type="button"
                    className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-extrabold transition lg:h-11 lg:w-11 lg:text-sm ${
                      isActive
                        ? "bg-[#562566] text-white"
                        : isAnswered
                          ? "bg-[#dff8ec] text-green-700"
                          : isSkipped
                            ? "bg-red-100 text-red-600"
                            : "bg-[#f4eef7] text-[#8a8490] hover:bg-[#eadfee]"
                    }`}
                    onClick={() => setCurrentIndex(index)}
                  >
                    {index + 1}
                  </button>
                );
              })}
            </div>

            <div className="mt-4 hidden grid-cols-3 gap-2 text-center text-xs font-bold">
              <div className="rounded-lg bg-[#F3ECF7] px-2 py-2 text-[#562566]">
                Current
              </div>
              <div className="rounded-lg bg-green-100 px-2 py-2 text-green-700">
                Answered
              </div>
              <div className="rounded-lg bg-gray-100 px-2 py-2 text-gray-600">
                Pending
              </div>
            </div>

            <button
              type="button"
              disabled={submitting || !isLastQuestion}
              className={`mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg px-4 py-3 font-extrabold shadow-sm disabled:cursor-not-allowed ${
                isLastQuestion
                  ? "bg-[#562566] text-white disabled:opacity-70"
                  : "bg-[#F3ECF7] text-[#8a8490]"
              }`}
              onClick={handleSubmitClick}
            >
              <Send className="h-4 w-4" />
              {submitting
                ? "Submitting..."
                : isLastQuestion
                  ? "Submit Test"
                  : "Submit on last question"}
            </button>
          </aside>
        </div>

        {showSubmitConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
            <div className="w-full max-w-sm rounded-lg bg-white p-5 text-center shadow-xl">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-50">
                <AlertCircle className="h-6 w-6 text-[#B46900]" />
              </div>
              <h2 className="text-lg font-extrabold text-[#2D1638]">
                Are you sure to submit the mock test?
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                {questions.length - answeredCount > 0
                  ? `${questions.length - answeredCount} questions abhi unanswered hain.`
                  : "Aapke answers final submit ho jayenge."}
              </p>
              <div className="mt-5 grid grid-cols-2 gap-3">
                <button
                  type="button"
                  className="rounded-lg bg-gray-100 px-4 py-3 text-sm font-bold text-gray-700"
                  onClick={() => setShowSubmitConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={submitting}
                  className="rounded-lg bg-[#562566] px-4 py-3 text-sm font-bold text-white disabled:opacity-70"
                  onClick={() => handleSubmit()}
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  /*
  return (
    <div className="min-h-screen bg-[#F7F8FC] px-3 py-4 sm:px-5 sm:py-6">
      <div className="mx-auto max-w-5xl">
        <button
          type="button"
          className="mb-4 inline-flex min-h-10 items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-bold text-[#4B2D83] shadow-sm"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <div className="rounded-[1.5rem] bg-[#562566] p-5 text-white shadow-[0_10px_24px_rgba(86,37,102,0.18)] sm:p-7">
          <p className="text-sm font-bold uppercase text-[#FFE3A5]">
            Mock Test
          </p>
          <h1 className="mt-2 text-2xl font-extrabold sm:text-3xl">
            {selectedInfo?.course?.planName || "Course Mock Tests"}
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-purple-100">
            Test information check karein, phir Start Test par click karke questions attempt karein.
          </p>
        </div>

        {mockTests.length ? (
          <div className="mt-5 grid gap-4">
            {mockTests.map((mockTest) => {
              const hasResult = Boolean(resultsByMockTestId[mockTest._id]);
              const isResultLoading = resultLoadingId === mockTest._id;
              const isLeaderboardLoading = leaderboardLoadingId === mockTest._id;

              return (
                <div
                  key={mockTest._id}
                  className="rounded-[1.5rem] border border-[#E8E3EE] bg-white p-4 shadow-sm sm:p-5"
                >
                  <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                      <h2 className="text-xl font-bold text-[#34163F]">
                        {mockTest.title}
                      </h2>
                      {mockTest.description && (
                        <p className="mt-2 text-sm text-gray-600">
                          {mockTest.description}
                        </p>
                      )}
                      <div className="mt-4 flex flex-wrap gap-2">
                        <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-3 py-1 text-sm font-bold text-purple">
                          <FileQuestion className="h-4 w-4" />
                          {mockTest.totalQuestions || 0} Questions
                        </span>
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-3 py-1 text-sm font-bold text-amber-700">
                          <Clock className="h-4 w-4" />
                          {mockTest.durationMinutes || 0} Minutes
                        </span>
                        {mockTest.category && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-3 py-1 text-sm font-bold text-green-700">
                            <BookOpen className="h-4 w-4" />
                            {mockTest.category}
                          </span>
                        )}
                        {hasResult && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-[#E7F8EE] px-3 py-1 text-sm font-bold text-green-700">
                            <CheckCircle2 className="h-4 w-4" />
                            Submitted
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex shrink-0 flex-col gap-2 sm:flex-row lg:flex-col xl:flex-row">
                      <button
                        type="button"
                        disabled={isLeaderboardLoading}
                        className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-[#562566] px-5 py-3 font-extrabold text-[#562566] disabled:opacity-70"
                        onClick={() => fetchLeaderboard(mockTest)}
                      >
                        <Trophy className="h-5 w-5" />
                        {isLeaderboardLoading ? "Loading..." : "Leaderboard"}
                      </button>
                      <button
                        type="button"
                        disabled={starting || isResultLoading}
                        className={`inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 font-extrabold shadow-sm disabled:opacity-70 ${
                          hasResult
                            ? "bg-[#562566] text-white"
                            : "bg-[#F6A91A] text-[#34163F] shadow-[0_10px_18px_rgba(245,166,35,0.28)]"
                        }`}
                        onClick={() =>
                          hasResult ? fetchMyResult(mockTest) : handleStartTest(mockTest)
                        }
                      >
                        {hasResult ? (
                          <Eye className="h-5 w-5" />
                        ) : (
                          <Play className="h-5 w-5 fill-[#34163F]" />
                        )}
                        {hasResult
                          ? isResultLoading
                            ? "Loading..."
                            : "View Result"
                          : starting
                            ? "Starting..."
                            : "Start Test"}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="mt-5 rounded-lg border border-gray-200 bg-white p-6 text-center shadow-sm">
            <h2 className="text-xl font-bold text-[#34163F]">
              No mock test available
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Is course ke liye abhi mock test add nahi hua hai.
            </p>
          </div>
        )}
      </div>
    </div>
  );
  */

  const courseTitle = pickFirst(
    selectedMockTest?.course?.planName,
    mockTests[0]?.course?.planName,
    "Course Mock Tests",
  );

  return (
    <div className="min-h-screen bg-purple-50 px-4 py-6">
      <div className="mx-auto max-w-5xl">
       

        {mockTests.length ? (
          <div className="mt-5 grid gap-4">
            {mockTests.map((mockTest) => {
              const hasResult =
                mockTest?.isSubmitted || Boolean(resultsByMockTestId[mockTest._id]);
              const courseName =
                mockTest?.course?.planName ||
                mockTest?.courseName ||
                mockTest?.examName ||
                courseTitle;

              return (
                <div
                  key={mockTest._id}
                  className="rounded-[20px] border border-[#E9DFEA] border-l-4 border-l-[#562566] bg-white p-4 shadow-sm sm:p-5"
                >
                  <div className="flex flex-col gap-4">
                    <div className="flex items-start justify-between gap-4">
                      <h2 className="break-words text-xl font-bold text-[#34163F]">
                        {mockTest.title || "Mock Test"}
                      </h2>
                      <span className="shrink-0 text-right text-sm font-bold text-[#6D6274] bg-gray-50 rounded-full px-3 py-1">
                        {courseName}
                      </span>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <span className="inline-flex items-center gap-1 rounded-full bg-gray-50 px-1.5 py-2 text-sm text-deepBlue">
                        <FileQuestion className="h-4 w-4" />
                        {mockTest.totalQuestions || mockTest.questionCount || 0} Questions
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-full border bg-gray-50 px-2 py-1 text-sm text-deepBlue">
                        <Clock className="h-4 w-4" />
                        {mockTest.durationMinutes || 0} Minutes
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-full bg-gray-50 px-2 py-1 text-sm text-deepBlue">
                        <Award className="h-4 w-4" />
                        {mockTest.totalQuestions || mockTest.marks || mockTest.maxMarks || 0} Marks
                      </span>
                    </div>

                    <div className="flex w-full flex-row gap-2">
                      <button
                        type="button"
                        className="inline-flex min-w-0 flex-1 items-center justify-center gap-2 rounded-full px-3 py-3 font-bold text-white transition bg-brightYellow "
                        onClick={() =>
                          navigate(`/dash/mock-test-details/${mockTest._id}`)
                        }
                      >
                        View Details
                        <ArrowRight className="h-5 w-5" />
                      </button>
                      <button
                        type="button"
                        className={`inline-flex min-w-0 flex-1 items-center justify-center gap-2 rounded-full px-3 py-3 font-bold transition hover:scale-[0.98] ${
                          hasResult
                            ? "bg-brightYellow  text-white"
                            : "bg-[#562566] text-white shadow-[0_10px_18px_rgba(245,166,35,0.28)]"
                        }`}
                        onClick={() =>
                          hasResult
                            ? navigate(`/dash/mock-test-result/${mockTest._id}`)
                            : handleStartTest(mockTest)
                        }
                      >
                        {hasResult ? (
                          <Eye className="h-5 w-5" />
                        ) : (
                          <Play className="h-4 w-4 fill-white" />
                        )}
                        {hasResult ? "View Result" : "Start Now"}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="mt-5 rounded-lg border border-gray-200 bg-white p-6 text-center shadow-sm">
            <AlertCircle className="mx-auto h-8 w-8 text-[#562566]" />
            <h2 className="mt-3 text-xl font-bold text-[#34163F]">
              No mock test available
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Is course ke liye abhi mock test add nahi hua hai.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
